import { useEffect, useRef, useState, useCallback } from 'react'
import { TeamColor } from 'types/match'

interface UseSpeechRecognitionControllerProps {
  handleCommand: (command: string[]) => void
  isController: boolean
  team1Color: TeamColor
  team2Color: TeamColor
}

export function useSpeechRecognitionController({
  handleCommand,
  isController,
  team1Color,
  team2Color
}: UseSpeechRecognitionControllerProps) {
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isListening, setIsListening] = useState(false)
  const [hasNetworkError, setHasNetworkError] = useState(false)

  const recognitionRef = useRef<any>(null)
  const recognitionActive = useRef(false)
  const isInCooldown = useRef(false)
  const isProcessingCommand = useRef(false)
  const keepAliveInterval = useRef<number | null>(null)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const restartAttempts = useRef(0)
  const maxRestartAttempts = useRef(10)
  const lastActiveTime = useRef<number>(Date.now())
  const inactivityCheckInterval = useRef<number | null>(null)

  // Detect mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

  // Keep-alive function to maintain connection
  const startKeepAlive = useCallback(() => {
    if (keepAliveInterval.current) return

    // Less aggressive checks since we're letting it end naturally
    const checkInterval = 1000 // Check every 1 second for both mobile and desktop

    keepAliveInterval.current = window.setInterval(() => {
      if (recognitionRef.current && !recognitionActive.current && !isInCooldown.current) {
        try {
          recognitionRef.current.start()
          restartAttempts.current = 0 // Reset attempts on successful restart
        } catch (error) {
          console.error('Keep-alive restart failed:', error)
          restartAttempts.current++
        }
      }
    }, checkInterval)
  }, [])

  // Stop keep-alive
  const stopKeepAlive = useCallback(() => {
    if (keepAliveInterval.current) {
      clearInterval(keepAliveInterval.current)
      keepAliveInterval.current = null
    }
  }, [])

  // Function to immediately stop and restart recognition to break speech continuity
  const restartRecognitionImmediately = useCallback(() => {
    if (recognitionRef.current && recognitionActive.current) {
      try {
        // Stop recognition immediately
        recognitionRef.current.stop()
        recognitionActive.current = false
        setIsListening(false)
        
        // Restart after 100ms to create a clean break
        setTimeout(() => {
          if (recognitionRef.current && !isInCooldown.current) {
            try {
              recognitionRef.current.start()
            } catch (error) {
              console.error('Failed to restart recognition after command:', error)
            }
          }
        }, 100)
      } catch (error) {
        console.error('Error stopping recognition after command:', error)
      }
    }
  }, [])

  // Request wake lock to prevent device sleep
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen')
      }
    } catch (error) {
      console.error('Wake Lock request failed:', error)
    }
  }, [])

  // Release wake lock
  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release()
        wakeLockRef.current = null
      } catch (error) {
        console.error('Wake Lock release failed:', error)
      }
    }
  }, [])

  // Initialize speech recognition only if this is a controller
  useEffect(() => {
    
    if (!isController) {
      return // Skip speech recognition for non-controllers
    }
    
    // Detect Samsung Internet Browser
    const isSamsungInternet = /SamsungBrowser/i.test(navigator.userAgent) || 
                             (navigator.userAgent.includes('Android') && navigator.userAgent.includes('Samsung'))
    
    if (isSamsungInternet) {
      setError('Voice commands may not work in Samsung Internet Browser. Please use Chrome, Firefox, or Safari.')
      // Auto-clear the error message after 10 seconds
      setTimeout(() => {
        setError(null)
      }, 10000)
      return
    }
    
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      setError('Speech recognition is not supported in your browser. Please use Chrome, Firefox, or Safari.')
      return
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    
    // Create recognition with default settings
    const recognitionOptions: any = {
      lang: 'en-US',
      continuous: true,
      interimResults: true,
      maxAlternatives: 1
    }
    
    const recognition = new SpeechRecognition(recognitionOptions)
    recognitionRef.current = recognition

    // Try to maximize continuous listening time
    if ('webkitSpeechRecognition' in window) {
      (recognition as any).continuous = true
      ;(recognition as any).interimResults = true
      ;(recognition as any).maxAlternatives = 1
      // Set a longer timeout if supported
      if ('timeout' in recognition) {
        (recognition as any).timeout = isMobile ? 30000 : 60000 // 30 seconds for mobile, 60 for desktop
      }
    }

    recognition.onstart = () => {
      recognitionActive.current = true
      setIsListening(true)
      setError(null)
      setHasNetworkError(false) // Clear network error when successfully restarted
      restartAttempts.current = 0 // Reset attempts on successful start
      // Start keep-alive when recognition starts
      startKeepAlive()
      // Request wake lock
      requestWakeLock()
      // Update last active time
      lastActiveTime.current = Date.now()
    }

    recognition.onend = () => {
      recognitionActive.current = false
      setIsListening(false)
      
      // Let microphone end naturally, then restart after 100ms on mobile
      if (!isInCooldown.current && restartAttempts.current < maxRestartAttempts.current) {
        // Use 100ms delay for mobile, let it end naturally
        const restartDelay = 100
        setTimeout(() => {
          if (!recognitionActive.current && !isInCooldown.current) {
            try {
              recognition.start()
            } catch (error) {
              console.error('Failed to restart speech recognition:', error)
              restartAttempts.current++
            }
          }
        }, restartDelay)
      }
      // Update last active time
      lastActiveTime.current = Date.now()
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // Update last active time when speech is detected
      lastActiveTime.current = Date.now()
      
      // Get the latest result
      const result = event.results[event.results.length - 1]
      const transcript = result[0].transcript.toLowerCase()
      const isFinal = result.isFinal
      const confidence = result[0].confidence

      // Update transcript for display
      setTranscript(transcript)

      // Check if this is a cancel command
      const isCancelCommand = transcript.includes('cancel') || transcript.includes('cancel set')
      
      // Only check cooldown if it's not a cancel command
      if (!isCancelCommand && isInCooldown.current) {
        return
      }

      // Define phonetic alternatives for colors (simplified like PHASE1)
      const colorPhonetics = {
        'red': 'romeo',
        'blue': 'bravo',
        'green': 'golf',
        'yellow': 'yankee',
        'orange': 'oscar',
        'purple': 'charlie'
      }

      // Get phonetic alternatives for current teams
      const team1Phonetic = colorPhonetics[team1Color] || ''
      const team2Phonetic = colorPhonetics[team2Color] || ''

      // SIMPLE, DIRECT COMMAND DETECTION (like PHASE1)
      if (transcript.includes(`${team1Color} point`) || 
          transcript.includes(`${team1Color}point`) ||
          transcript.includes(`${team1Color}'s point`) || 
          transcript.includes(`${team1Color}s point`) ||
          transcript.includes(`${team1Phonetic} point`) ||
          transcript.includes(`${team1Phonetic}point`) ||
          transcript.includes(`${team1Phonetic}'s point`) || 
          transcript.includes(`${team1Phonetic}s point`) ||
          // Additional blue team variations
          (team1Color === 'blue' && (
            transcript.includes('blue') ||
            transcript.includes('bravo') ||
            transcript.includes('view') ||
            transcript.includes('flew') ||
            transcript.includes('flu') ||
            transcript.includes('through') ||
            transcript.includes('true') ||
            transcript.includes('lose') ||
            transcript.includes('brother') ||
            transcript.includes('play') ||
            transcript.includes('new') ||
            transcript.includes('three') ||
            transcript.includes('3.')
          )) ||
          // Additional yellow team variations
          (team1Color === 'yellow' && (
            transcript.includes('hello') ||
            transcript.includes('yellow') ||
            transcript.includes('yankee')
          )) ||
          // Additional green team variations
          (team1Color === 'green' && (
            transcript.includes('green') ||
            transcript.includes('cream') ||
            transcript.includes('seen') ||
            transcript.includes('seem') ||
            transcript.includes('golf') ||
            transcript.includes('screen')
          )) ||
          // Additional orange team variations
          (team1Color === 'orange' && (
            transcript.includes('orange') ||
            transcript.includes('oscar') ||
            transcript.includes('range') ||
            transcript.includes('armed') ||
            transcript.includes('or end') ||
            transcript.includes('are in')
          )) ||
          // Additional purple team variations
          (team1Color === 'purple' && (
            transcript.includes('purple') ||
            transcript.includes('triple') ||
            transcript.includes('charlie')
          )) ||
          // Additional red team variations
          (team1Color === 'red' && (
            transcript.includes('red') ||
            transcript.includes('romeo') ||
            transcript.includes('read') ||
            transcript.includes('tread') ||
            transcript.includes('trade') ||
            transcript.includes('rex') ||
            transcript.includes('head') ||
            transcript.includes('great')
          ))
      ) {
        handleCommand([`${team1Color} point`])
        setTranscript('') // Clear transcript immediately
        restartRecognitionImmediately() // Stop and restart to break speech continuity
        return
      } else if (transcript.includes(`${team2Color} point`) || 
                 transcript.includes(`${team2Color}point`) ||
                 transcript.includes(`${team2Color}'s point`) || 
                 transcript.includes(`${team2Color}s point`) ||
                 transcript.includes(`${team2Phonetic} point`) ||
                 transcript.includes(`${team2Phonetic}point`) ||
                 transcript.includes(`${team2Phonetic}'s point`) || 
                 transcript.includes(`${team2Phonetic}s point`) ||
                 // Additional blue team variations
                 (team2Color === 'blue' && (
                   transcript.includes('blue') ||
                   transcript.includes('bravo') ||
                   transcript.includes('view') ||
                   transcript.includes('flew') ||
                   transcript.includes('flu') ||
                   transcript.includes('through') ||
                   transcript.includes('true') ||
                   transcript.includes('lose') ||
                   transcript.includes('brother') ||
                   transcript.includes('play') ||
                   transcript.includes('new')
                 )) ||
                 // Additional yellow team variations
                 (team2Color === 'yellow' && (
                   transcript.includes('hello') ||
                   transcript.includes('yellow') ||
                   transcript.includes('yankee')
                 )) ||
                 // Additional green team variations
                 (team2Color === 'green' && (
                   transcript.includes('green') ||
                   transcript.includes('cream') ||
                   transcript.includes('seen') ||
                   transcript.includes('seem') ||
                   transcript.includes('golf') ||
                   transcript.includes('screen')
                 )) ||
                 // Additional orange team variations
                 (team2Color === 'orange' && (
                   transcript.includes('orange') ||
                   transcript.includes('oscar') ||
                   transcript.includes('range') ||
                   transcript.includes('power') ||
                   transcript.includes('armed') ||
                   transcript.includes('or end') ||
                   transcript.includes('are in')
                 )) ||
                 // Additional purple team variations
                 (team2Color === 'purple' && (
                   transcript.includes('purple') ||
                   transcript.includes('triple') ||
                   transcript.includes('flip') ||
                   transcript.includes('charlie')
                 )) ||
                 // Additional red team variations
                 (team2Color === 'red' && (
                   transcript.includes('red') ||
                   transcript.includes('romeo') ||
                   transcript.includes('read') ||
                   transcript.includes('tread') ||
                   transcript.includes('trade') ||
                   transcript.includes('rex') ||
                   transcript.includes('head') ||
                   transcript.includes('great')
                 ))
      ) {
        handleCommand([`${team2Color} point`])
        setTranscript('') // Clear transcript immediately
        restartRecognitionImmediately() // Stop and restart to break speech continuity
        return
      } else if (transcript === 'reverse' ||
                 transcript.includes('reverse point') || 
                 transcript.includes('reversepoint') ||
                 transcript.includes('reverse a point') ||
                 transcript.includes('reverse one point') ||
                 transcript.includes('reverse the point') ||
                 transcript.includes('reverse another point') ||
                 transcript.includes('reverse 1.') ||
                 transcript.includes('verse point') ||
                 transcript.includes('versepoint') ||
                 transcript.includes('vs point') ||
                 transcript.includes('vspoint')) {
        handleCommand(['reverse point'])
        setTranscript('') // Clear transcript immediately
        restartRecognitionImmediately() // Stop and restart to break speech continuity
        return
      } else if (isCancelCommand) {
        handleCommand(['cancel'])
        setTranscript('') // Clear transcript immediately
        restartRecognitionImmediately() // Stop and restart to break speech continuity
        return
      } else if (transcript.includes('reset match') || 
                 transcript.includes('resetmatch') ||
                 transcript.includes('reset mach') ||
                 transcript.includes('resetmach') ||
                 transcript.includes('reset mac') ||
                 transcript.includes('resetmac') ||
                 transcript.includes('reset much') ||
                 transcript.includes('resetmuch') ||
                 transcript.includes('reset mush') ||
                 transcript.includes('resetmush') ||
                 transcript.includes('reset mash') ||
                 transcript.includes('resetmash') ||
                 transcript.includes('reset math') ||
                 transcript.includes('resetmath') ||
                 transcript.includes('reset max') ||
                 transcript.includes('resetmax') ||
                 transcript.includes('reset the match') ||
                 transcript.includes('recent match') ||
                 transcript.includes('recentmatch')) {
        handleCommand(['reset match'])
        setTranscript('') // Clear transcript immediately
        restartRecognitionImmediately() // Stop and restart to break speech continuity
        return
      } else if (transcript.includes('confirm') ||
                 transcript.includes('confirmed') ||
                 transcript.includes('confirms') ||
                 transcript.includes('confirming') ||
                 transcript.includes('confirm reset')) {
        handleCommand(['confirm'])
        setTranscript('') // Clear transcript immediately
        restartRecognitionImmediately() // Stop and restart to break speech continuity
        return
      }
    }

    recognition.onnomatch = () => {
      // No speech match found
    }

    recognition.onerror = (event: any) => {
      const errorMessages: { [key: string]: string } = {
        'audio-capture': 'No microphone was found. Please ensure your microphone is connected.',
        'network': 'Network error occurred. Please check your connection.',
        'not-allowed': 'Microphone access was denied. Please allow microphone access.',
        'service-not-allowed': 'Speech recognition service is not allowed.',
        'bad-grammar': 'Speech recognition grammar error.',
        'language-not-supported': 'Language not supported.',
        'no-speech': 'No speech detected. Please try speaking again.',
        'aborted': 'Speech recognition was aborted.',
      }
      
      const errorMessage = errorMessages[event.error]
      if (errorMessage) {
        setError(errorMessage)
      }
      setIsListening(false)
      
      // Set network error state to show modal
      if (event.error === 'network') {
        setHasNetworkError(true)
      }
      
      console.error('Speech recognition error:', event.error, errorMessage)
      
      // On mobile, restart even on "no-speech" errors as they're common
      const shouldRestart = isMobile ? 
        (event.error !== 'not-allowed' && event.error !== 'service-not-allowed') :
        (event.error !== 'no-speech' && event.error !== 'not-allowed' && event.error !== 'service-not-allowed')
      
      if (shouldRestart && !isInCooldown.current) {
        setTimeout(() => {
          if (!recognitionActive.current && !isInCooldown.current) {
            try {
              recognition.start()
              setError(null) // Clear error when restarting
            } catch (error) {
              console.error('Failed to restart speech recognition after error:', error)
            }
          }
        }, 100) // Use consistent 100ms delay for both mobile and desktop
      }
    }

    // Start listening automatically
    try {
      recognition.start()
    } catch (error) {
      console.error('Failed to start speech recognition:', error)
      setError('Failed to start speech recognition')
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
        recognitionActive.current = false
      }
      stopKeepAlive()
      releaseWakeLock()
    }
  }, [isController, team1Color, team2Color, handleCommand, startKeepAlive, stopKeepAlive,
    requestWakeLock, releaseWakeLock, restartRecognitionImmediately])

  // Add visibility change handler to restart recognition when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && recognitionRef.current && !recognitionActive.current && !isInCooldown.current) {
        try {
          recognitionRef.current.start()
        } catch (error) {
          console.error('Failed to restart recognition on visibility change:', error)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Add page focus handler to restart recognition when page regains focus
  useEffect(() => {
    const handleFocus = () => {
      if (recognitionRef.current && !recognitionActive.current && !isInCooldown.current) {
        try {
          recognitionRef.current.start()
        } catch (error) {
          console.error('Failed to restart recognition on focus:', error)
        }
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
        recognitionActive.current = false
      }
      stopKeepAlive()
      releaseWakeLock()
    }
  }, [stopKeepAlive, releaseWakeLock])

  return {
    transcript,
    error,
    isListening,
    hasNetworkError
  }
}